/**
 * RunnerV3Detail — Single machine detail page (ID Visual V2)
 */
import { useState, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../api/client';
import { isLoggedIn } from '../stores/auth.store';
import { LoginPage } from './Login';
import { Terminal } from '../components/Terminal';
import { OpenClawInstaller } from '../components/OpenClawInstaller';
import { OpenClawUninstaller } from '../components/OpenClawUninstaller';
import { OpenClawConfig } from '../components/OpenClawConfig';
import { AgentGeneral } from '../components/AgentGeneral';
import { OpenClawBrain } from '../components/OpenClawBrain';
import { AgentCredentials } from '../components/AgentCredentials';
import { OpenClawSkills } from '../components/OpenClawSkills';
import { SkillsCategoryTabs } from '../components/SkillsCategoryTabs';
import { MachineDetailPanel } from '../components/MachineDetailPanel';
import { SetupModal } from '../components/SetupModal';
import { useLiveSkillsFromOpenClaw, type LiveSkill } from '../hooks/useLiveSkillsFromOpenClaw';
import { SkeletonBar, Button } from '../components/ui';
import { T } from '../styles/tokens';

interface RunnerPermissions {
  desktop?: boolean;
  documents?: boolean;
  downloads?: boolean;
  screen_capture?: boolean;
  accessibility?: boolean;
  microphone?: boolean;
}

interface RunnerV3 {
  id: string;
  name: string;
  os: string;
  arch: string;
  mode: 'headless' | 'ui';
  status: 'online' | 'offline' | 'error';
  version: string;
  last_seen_at?: string;
  created_at?: string;
  has_openclaw?: number;
  openclaw_path?: string | null;
  openclaw_base?: string | null;
  has_docker?: number;
  docker_version?: string | null;
  node_version?: string | null;
  permissions?: string | RunnerPermissions;
}

const OPENCLAW_PANEL_PORT = 18789;

function isOnline(r: RunnerV3): boolean {
  if (r.status !== 'online' || !r.last_seen_at) return false;
  return Date.now() - new Date(r.last_seen_at).getTime() < 45_000;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60_000) return `${Math.round(diff / 1000)}s atrás`;
  if (diff < 3600_000) return `${Math.round(diff / 60000)}min atrás`;
  if (diff < 86400_000) return `${Math.round(diff / 3600000)}h atrás`;
  return new Date(dateStr).toLocaleString('pt-BR');
}

function osIcon(os: string, size = 24): any {
  if (os === 'macos' || os === 'darwin') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ color: T.text }}>
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
  if (os === 'linux') return 'Lin';
  if (os === 'windows') return 'Win';
  return 'PC';
}

function CopyCommand({ cmd }: { cmd: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(cmd).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '10px 14px', background: T.bgSubtle, borderRadius: 10,
      border: `1px solid ${T.border}`,
      fontFamily: T.fontMono, fontSize: 11, color: T.text,
      overflowX: 'auto', whiteSpace: 'nowrap',
    }}>
      <span style={{ color: T.accent }}>$</span>
      <code style={{ flex: 1, userSelect: 'all' as any }}>{cmd}</code>
      <Button variant="ghost" onClick={copy} style={{
        background: copied ? T.successBg : T.border,
        border: `1px solid ${copied ? T.success : T.border}`,
        borderRadius: 8, padding: '4px 10px',
        color: copied ? T.success : T.textMuted, cursor: 'pointer',
        fontSize: 11, fontWeight: 600, flexShrink: 0, transition: 'all 0.15s',
      }}>{copied ? '' : 'Copiar'}</Button>
    </div>
  );
}

/* ═══ Action Button helper ═══ */
function ActionBtn({ active, color, onClick, disabled, children, title }: {
  active?: boolean; color?: string; onClick: () => void; disabled?: boolean; children: any; title?: string;
}) {
  const c = color || T.primary;
  return (
    <Button variant="ghost" title={title} onClick={onClick} disabled={disabled} style={{
      padding: '8px 14px', borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
      border: active ? `1px solid ${c}40` : `1px solid ${T.border}`,
      background: active ? `${c}15` : T.bgCard,
      color: active ? c : T.textMuted,
      transition: 'all 0.15s', opacity: disabled ? 0.5 : 1,
    }}>{children}</Button>
  );
}

export function RunnerV3Detail({ id }: { id: string }) {
  if (!isLoggedIn.value) return <LoginPage />;

  const [runner, setRunner] = useState<RunnerV3 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [showTerminal, setShowTerminal] = useState(false);
  const [showInstaller, setShowInstaller] = useState(false);
  const [showUninstaller, setShowUninstaller] = useState(false);
  const [installBusy, setInstallBusy] = useState(false);
  const [openclawInstalled, setOpenclawInstalled] = useState<boolean | null>(null);
  const [showOpenclawConfig, setShowOpenclawConfig] = useState(false);
  const [showBrain, setShowBrain] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showSkills, setShowSkills] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<{
    name: string;
    emoji?: string;
    status: 'online' | 'offline' | 'synced';
    defaultProvider?: string;
    defaultModel?: string;
    lastSync?: Date;
  } | null>(null);
  const [agentPanelTab, setAgentPanelTab] = useState<'details' | 'config' | 'brain' | 'integrations' | 'skills'>('details');
  const [showAgentPanel, setShowAgentPanel] = useState(false);
  const [copyingToken, setCopyingToken] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [permsOpen, setPermsOpen] = useState(false);
  const [sectionOpen, setSectionOpen] = useState<{ servidor: boolean; runner: boolean; openclaw: boolean }>({ servidor: false, runner: false, openclaw: true });
  const toggleSection = (key: 'servidor' | 'runner' | 'openclaw') => setSectionOpen(prev => ({ ...prev, [key]: !prev[key] }));
  const [errorAlertDismissed, setErrorAlertDismissed] = useState(false);
  const [errorAlertExpanded, setErrorAlertExpanded] = useState(false);
  const [logEntries, setLogEntries] = useState<any[]>([]);
  const [logFilter, setLogFilter] = useState<'all' | 'errors'>('all');
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [agentRequests, setAgentRequests] = useState<any[]>([]);
  const [loadingAgentRequests, setLoadingAgentRequests] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [heartbeats, setHeartbeats] = useState<{ created_at: string; status: string }[]>([]);
  const [cloudMachineId, setCloudMachineId] = useState<string | null>(null);
  const [machineIp, setMachineIp] = useState<string | null>(null);
  const [, setTick] = useState(0);
  const [uninstallingSkill, setUninstallingSkill] = useState<string | null>(null);
  const [skillsHighlight, setSkillsHighlight] = useState(false);
  const intervalRef = useRef<any>(null);
  const tickRef = useRef<any>(null);
  const skillsSectionRef = useRef<HTMLDivElement>(null);

  // Preload removed — each panel loads on open for stability

  const fetchRunner = async () => {
    try {
      const res = await api.get(`/runner-v3/${id}`) as any;
      if (res.success && res.data) {
        setRunner(res.data);
        if (res.data.has_openclaw !== undefined && openclawInstalled === null) {
          setOpenclawInstalled(!!res.data.has_openclaw);
        }
      } else setError('Máquina não encontrada');
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar');
    } finally { setLoading(false); }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await api.get(`/runner-v3/${id}/logs?limit=100`) as any;
      if (res.success && res.data?.logs) setLogEntries(res.data.logs);
    } catch {}
    setLogsLoading(false);
  };

  const checkOpenClaw = async () => {
    try {
      const res = await api.post(`/runner-v3/${id}/terminal/exec`, {
        command: '(export PATH="/opt/homebrew/bin:/opt/homebrew/sbin:/usr/local/bin:/usr/bin:$HOME/.nvm/versions/node/*/bin:$HOME/.volta/bin:$PATH"; which openclaw 2>/dev/null && echo "INSTALLED") || (docker ps --format "{{.Names}} {{.Image}}" 2>/dev/null | grep -qi openclaw && echo "INSTALLED") || (test -d /data/.openclaw && ls /data/.openclaw/ 2>/dev/null | grep -q . && echo "INSTALLED") || (test -d $HOME/.openclaw && ls $HOME/.openclaw/ 2>/dev/null | grep -q . && echo "INSTALLED") || echo "NOT_INSTALLED"'
      }) as any;
      if (!res.success || !res.data?.id) return;
      const cmdId = res.data.id;
      for (let i = 0; i < 20; i++) {
        await new Promise(r => setTimeout(r, 1000));
        try {
          const cmd = await api.get(`/runner-v3/${id}/terminal/output/${cmdId}`) as any;
          if (cmd && (cmd.status === 'completed' || cmd.status === 'error')) {
            setOpenclawInstalled(cmd.output?.includes('INSTALLED') && !cmd.output?.includes('NOT_INSTALLED'));
            return;
          }
        } catch {}
      }
    } catch {}
  };

  useEffect(() => {
    fetchRunner();
    intervalRef.current = setInterval(fetchRunner, 15000);
    tickRef.current = setInterval(() => setTick(t => t + 1), 5000);
    return () => { clearInterval(intervalRef.current); clearInterval(tickRef.current); };
  }, [id]);

  // Fetch agent requests when cloudMachineId is known
  useEffect(() => {
    if (cloudMachineId) {
      fetchAgentRequests();
      const iv = setInterval(fetchAgentRequests, 15000);
      return () => clearInterval(iv);
    }
  }, [cloudMachineId]);

  useEffect(() => {
    if (runner && isOnline(runner) && (runner.os === 'macos' || runner.os === 'darwin')) {
      checkOpenClaw();
    }
  }, [runner?.id, runner?.status]);

  // Cerebros/models/skills fetching moved to CreateAgentModal component

  // Fetch agent creation requests
  async function fetchAgentRequests() {
    if (!cloudMachineId) return;
    setLoadingAgentRequests(true);
    try {
      const data = await api.get<{ requests: any[] }>(`/cloud/machines/${cloudMachineId}/agent-requests`);
      setAgentRequests(data.requests || []);
    } catch { /* non-fatal */ }
    finally { setLoadingAgentRequests(false); }
  }

  // createNewAgent moved to CreateAgentModal component

  // Load agent info from OpenClaw config
  const loadAgentFromOpenClaw = async () => {
    const { runCommandDirect } = await import('../lib/runCommand.js');
    let resolvedName = 'Agente';
    let resolvedModel = 'Padrão';

    // Primary: read directly from OpenClaw runtime via `openclaw agents list`
    try {
      const agentOutput = await runCommandDirect(api, id, 'openclaw agents list 2>/dev/null');
      // Parse output like: "- main (default) (AgentName)\n  Identity: AgentName (config)\n  Model: anthropic/claude-haiku-4-5"
      const nameMatch = agentOutput.match(/^- \w+.*?\(([^)]+)\)\s*$/m);
      const identityMatch = agentOutput.match(/Identity:\s*(.+?)\s*\(/m);
      const modelMatch = agentOutput.match(/Model:\s*(.+)$/m);
      
      if (identityMatch?.[1]) resolvedName = identityMatch[1].trim();
      else if (nameMatch?.[1]) resolvedName = nameMatch[1].trim();
      
      if (modelMatch?.[1]) resolvedModel = modelMatch[1].trim().replace(/^.*\//, '');
    } catch (e) {
      console.warn('openclaw agents list falhou, tentando openclaw.json:', e);
    }

    // Fallback 1: read openclaw.json file
    if (resolvedName === 'Agente' || resolvedName === 'Assistant' || !resolvedName) {
      try {
        const output = await runCommandDirect(api, id, 'cat ~/.openclaw/openclaw.json 2>/dev/null');
        const config = JSON.parse(output);
        const agentFromList = config.agents?.list?.[0];
        const jsonName = agentFromList?.identity?.name || agentFromList?.name || config.agentName;
        if (jsonName && jsonName !== 'Assistant') resolvedName = jsonName;
        if (config.agents?.defaults?.model?.primary) {
          resolvedModel = config.agents.defaults.model.primary.replace(/^elon\//, '');
        }
      } catch {}
    }

    // Fallback 2: cloud_machines.agent_name from API
    if ((resolvedName === 'Agente' || resolvedName === 'Assistant' || !resolvedName) && cloudMachineId) {
      try {
        const cmRes = await api.get<any>(`/cloud/machines/${cloudMachineId}`);
        const cmName = cmRes?.machine?.agent_name || cmRes?.agent_name;
        if (cmName) resolvedName = cmName;
      } catch {}
    }

    setSelectedAgent({
      name: resolvedName || 'Agente',
      emoji: '',
      status: 'synced',
      defaultProvider: resolvedModel,
      lastSync: new Date(),
    });
  };

  useEffect(() => {
    if (openclawInstalled && runner && isOnline(runner)) {
      loadAgentFromOpenClaw();
    }
  }, [openclawInstalled, runner?.status]);

  // Look up cloud machine associated with this runner (by matching name)
  useEffect(() => {
    if (!runner) return;
    (async () => {
      try {
        const res = await api.get('/cloud/machines') as any;
        const machines = res?.machines || res?.data?.machines || [];
        const match = machines.find((m: any) => {
          const hostname = m.hostname || m.name || '';
          return hostname === runner.name ||
            runner.name?.includes(hostname.split('.')[0] || '__nomatch__') ||
            m.name === runner.name;
        });
        if (match) {
          setCloudMachineId(match.id);
          if (match.ipv4_address) setMachineIp(match.ipv4_address);
        }
      } catch {}
    })();
  }, [runner?.id, runner?.name]);

  const fetchHeartbeats = async () => {
    try {
      const res = await api.get(`/runner-v3/${id}/heartbeats?limit=30`) as any;
      if (res.success && res.data?.heartbeats) setHeartbeats(res.data.heartbeats);
    } catch {}
  };

  useEffect(() => {
    fetchHeartbeats();
    const iv = setInterval(fetchHeartbeats, 30000);
    return () => clearInterval(iv);
  }, [id]);

  // Always fetch logs on mount (for error alert) + refresh when logs panel open
  useEffect(() => {
    fetchLogs();
    const iv = setInterval(fetchLogs, showLogs ? 10000 : 60000);
    return () => clearInterval(iv);
  }, [showLogs, id]);

  // Each panel (Config/Brain/Integrations) loads its own data when opened

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/runner-v3/${id}`);
      route('/agents');
    } catch (e: any) {
      alert(e?.message || 'Erro ao excluir');
    }
    setDeleting(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      let ok = 0;
      for (let i = 0; i < 3; i++) {
        await new Promise(r => setTimeout(r, 2000));
        await fetchRunner();
        if (runner?.last_seen_at && Date.now() - new Date(runner.last_seen_at).getTime() < 45_000) ok++;
      }
      setTestResult(ok >= 2
        ? { ok: true, msg: 'Máquina ativa e respondendo.' }
        : { ok: false, msg: 'Heartbeat irregular. Verifique se o app está rodando.' }
      );
    } catch (e: any) {
      setTestResult({ ok: false, msg: `${e?.message || 'Erro'}` });
    }
    setTesting(false);
  };

  if (loading) {
    return (
      <div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>
    );
  }

  if (error || !runner) {
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 24, marginBottom: 16, color: T.textMuted }}>Não encontrado</div>
        <p style={{ fontSize: 16, color: T.text }}>{error || 'Máquina não encontrada'}</p>
        <Button variant="ghost" onClick={() => route('/agents')} class="btn btn-primary" style={{ marginTop: 16 }}>← Voltar</Button>
      </div>
    );
  }

  const online = isOnline(runner);

  // Parse permissions
  let perms: RunnerPermissions = {};
  try {
    perms = typeof runner.permissions === 'string'
      ? JSON.parse(runner.permissions || '{}')
      : (runner.permissions || {});
  } catch { perms = {}; }

  // Permission summary dots
  const permList: { key: keyof RunnerPermissions; label: string; icon: string; desc: string }[] = [
    { key: 'desktop', label: 'Mesa (Desktop)', icon: '•', desc: 'Acesso a arquivos na Área de Trabalho' },
    { key: 'documents', label: 'Documentos', icon: '•', desc: 'Acesso à pasta Documentos' },
    { key: 'downloads', label: 'Downloads', icon: '•', desc: 'Acesso à pasta Downloads' },
    { key: 'screen_capture', label: 'Captura de Tela', icon: '•', desc: 'Tirar screenshots e gravar tela' },
    { key: 'accessibility', label: 'Teclado e Mouse', icon: '•', desc: 'Controlar mouse e teclado remotamente' },
    { key: 'microphone', label: 'Áudio', icon: '•', desc: 'Acesso ao microfone' },
  ];

  const grantedCount = permList.filter(p => !!perms[p.key]).length;

  // Uptime bar computation
  const GAP_MS = 45_000;
  const SLOTS = 30;
  const uptimeSlots: ('online' | 'offline' | 'empty')[] = [];
  if (heartbeats.length === 0) {
    for (let i = 0; i < SLOTS; i++) uptimeSlots.push('empty');
  } else {
    const pad = SLOTS - heartbeats.length;
    for (let i = 0; i < pad; i++) uptimeSlots.push('empty');
    for (let i = 0; i < heartbeats.length; i++) {
      const hb = heartbeats[i];
      let hasGapBefore = false;
      if (i > 0) {
        const prev = new Date(heartbeats[i - 1].created_at).getTime();
        const curr = new Date(hb.created_at).getTime();
        hasGapBefore = (curr - prev) > GAP_MS;
      }
      const isLast = i === heartbeats.length - 1;
      const isStale = isLast && (Date.now() - new Date(hb.created_at).getTime()) > GAP_MS;
      uptimeSlots.push((hasGapBefore || hb.status === 'offline' || hb.status === 'error' || isStale) ? 'offline' : 'online');
    }
  }
  const onlineCount = uptimeSlots.filter(s => s === 'online').length;
  const totalReal = uptimeSlots.filter(s => s !== 'empty').length;
  const uptimePct = totalReal > 0 ? Math.round((onlineCount / totalReal) * 100) : (online ? 100 : 0);

  // Close all openclaw panels helper
  const closeAllOC = () => { setShowInstaller(false); setShowUninstaller(false); setShowOpenclawConfig(false); setShowBrain(false); setShowIntegrations(false); setShowSkills(false); };

  // Live skills hook
  const {
    skills, loading: skillsLoading, error: skillsError,
    syncedAt, runnerStatus: skillsRunnerStatus,
    refetch: refetchSkills, uninstallSkill,
  } = useLiveSkillsFromOpenClaw(id);

  const handleManageSkills = () => {
    setShowSkills(s => !s); // Toggle Skills section
    if (!showSkills && skillsSectionRef.current) {
      // If opening, scroll into view after a brief delay to let the DOM render
      setTimeout(() => {
        skillsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setSkillsHighlight(true);
        setTimeout(() => setSkillsHighlight(false), 2000);
      }, 100);
    }
  };

  const handleUninstallSkill = async (skillName: string) => {
    setUninstallingSkill(skillName);
    await uninstallSkill(skillName);
    setUninstallingSkill(null);
  };

  const handleCopyGatewayToken = async () => {
    if (!cloudMachineId || copyingToken) return;
    setCopyingToken(true);
    setTokenError('');
    setTokenCopied(false);
    try {
      const res = await api.get(`/cloud/machines/${cloudMachineId}/openclaw-config`) as any;
      const token = res?.data?.gatewayToken || res?.gatewayToken;
      if (!token) {
        setTokenError('Token não encontrado. Configure o OpenClaw primeiro.');
        return;
      }
      await navigator.clipboard.writeText(token);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    } catch (e: any) {
      setTokenError(e?.message || 'Erro ao copiar token');
      setTimeout(() => setTokenError(''), 3000);
    } finally {
      setCopyingToken(false);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: 720, margin: '0 auto', position: 'relative' }}>
      {/* Aurora glow */}
      <div style={{
        position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)',
        width: 400, height: 250, pointerEvents: 'none', zIndex: 0,
        background: online
          ? `radial-gradient(ellipse, ${T.primary} 0%, ${T.accentBgSubtle} 40%, transparent 70%)`
          : 'radial-gradient(ellipse, rgba(167,179,199,0.04) 0%, transparent 60%)',
      }} />

      {/* Back */}
      <Button variant="ghost" onClick={() => route('/agents')} style={{
        background: 'none', border: 'none', color: T.textMuted,
        cursor: 'pointer', fontSize: 14, padding: '4px 0', marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 6, position: 'relative', zIndex: 1,
        fontWeight: 500, transition: 'color 0.15s',
      }}
        onMouseEnter={(e: any) => e.currentTarget.style.color = T.primary}
        onMouseLeave={(e: any) => e.currentTarget.style.color = T.textMuted}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        Meus Agentes
      </Button>

      {/* ═══ SECTION STYLES ═══ */}

      {/* ═══ 1. SERVIDOR SECTION — always visible ═══ */}
      {cloudMachineId && (
        <div style={{
          background: T.bgCard, border: `1px solid ${T.border}`,
          borderRadius: 14, marginBottom: 16, overflow: 'hidden',
        }}>
          <div onClick={() => toggleSection('servidor')} style={{
            padding: '14px 20px', borderBottom: sectionOpen.servidor ? `1px solid ${T.border}` : 'none',
            background: T.bgSubtle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none',
          }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text, flex: 1 }}>Servidor</span>
            <span style={{ fontSize: 12, color: T.textMuted, transition: 'transform 0.2s', transform: sectionOpen.servidor ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
          </div>
          {sectionOpen.servidor && <div style={{ padding: '16px 20px' }}>
            <MachineDetailPanel machineId={cloudMachineId} />
          </div>}
        </div>
      )}

      {/* ═══ 2. ELON RUNNER SECTION — always visible ═══ */}
      <div style={{
        background: T.bgCard, border: `1px solid ${T.border}`,
        borderRadius: 14, marginBottom: 16, overflow: 'hidden',
      }}>
        <div onClick={() => toggleSection('runner')} style={{
          padding: '14px 20px', borderBottom: sectionOpen.runner ? `1px solid ${T.border}` : 'none',
          background: T.bgSubtle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none',
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.text, flex: 1 }}>Elon Runner</span>
          <span style={{ fontSize: 12, color: T.textMuted, transition: 'transform 0.2s', transform: sectionOpen.runner ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
        </div>
        {sectionOpen.runner && <div style={{ padding: '16px 20px' }}>
          {/* Runner info: hostname + status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: online ? T.primaryBg : T.bgSecondary,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, flexShrink: 0,
              border: `1px solid ${online ? T.primary : T.border}`,
            }}>
              {osIcon(runner.os)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ fontSize: 20, fontWeight: 800, color: T.text, margin: '0 0 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '-0.3px' }}>{runner.name}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span title={online ? 'Máquina conectada e respondendo normalmente' : 'Máquina desconectada — sem resposta recente'} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '2px 10px', borderRadius: T.radiusFull, fontSize: 11, fontWeight: 600,
                  background: online ? T.successBg : 'rgba(149,165,166,0.1)',
                  color: online ? T.success : T.textMuted,
                  border: `1px solid ${online ? T.success : 'rgba(149,165,166,0.15)'}`,
                  cursor: 'help',
                }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: online ? T.success : T.textMuted,
                    ...(online ? { animation: 'pulse 2s infinite' } : {}),
                  }} />
                  {online ? 'Online' : 'Offline'}
                </span>
                <span title="Tempo desde o último heartbeat" style={{ fontSize: 11, color: T.textMuted, cursor: 'help' }}>
                  {runner.last_seen_at ? timeAgo(runner.last_seen_at) : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Info chips */}
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2, marginBottom: 14 }}>
            {[
              { label: `${runner.os} ${runner.arch}`, icon: '', tooltip: `Sistema: ${runner.os} • Arch: ${runner.arch}` },
              { label: runner.mode === 'ui' ? 'UI' : 'Headless', icon: '', tooltip: runner.mode === 'ui' ? 'Modo UI — captura + controle remoto' : 'Modo Headless — só comandos' },
              { label: `v${runner.version}`, icon: '', tooltip: 'Versão do WhiteLabelClaw' },
              ...(runner.created_at ? [{ label: new Date(runner.created_at).toLocaleDateString('pt-BR'), icon: '', tooltip: `Conectada em ${new Date(runner.created_at).toLocaleDateString('pt-BR')}` }] : []),
              ...(runner.node_version ? [{ label: runner.node_version, icon: '', tooltip: `Node.js ${runner.node_version}` }] : []),
              ...(runner.has_docker ? [{ label: runner.docker_version ? `Docker ${runner.docker_version}` : 'Docker', icon: '', tooltip: runner.docker_version ? `Docker ${runner.docker_version} instalado` : 'Docker instalado' }] : [{ label: 'Sem Docker', icon: '', tooltip: 'Docker não instalado', dimmed: true }]),
            ].map((chip: any, i: number) => (
              <span key={i} title={chip.tooltip} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 500,
                background: chip.dimmed ? T.bgSubtle : T.bgCard,
                color: chip.dimmed ? `${T.textMuted})` : T.textMuted,
                border: `1px solid ${T.borderSubtle}`,
                whiteSpace: 'nowrap', cursor: 'help', flexShrink: 0,
                opacity: chip.dimmed ? 0.5 : 1,
              }}>
                <span style={{ fontSize: 12 }}>{chip.icon}</span>
                {chip.label}
              </span>
            ))}
            {/* Permission summary dots */}
            <span title={`Permissões: ${grantedCount}/${permList.length} ativas`} style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              padding: '4px 10px', borderRadius: 8, fontSize: 11,
              background: T.bgCard, border: `1px solid ${T.borderSubtle}`,
              cursor: 'help', flexShrink: 0,
            }}>
              {permList.map((p, i) => (
                <span key={i} style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: perms[p.key] ? T.success : T.danger,
                }} />
              ))}
            </span>
          </div>

          {/* Uptime bar */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              display: 'flex', gap: 2, height: 14, alignItems: 'stretch',
              background: T.bgCard, borderRadius: 6, padding: '2px 3px',
            }}>
              {uptimeSlots.map((s, i) => (
                <div key={i} style={{
                  flex: 1, borderRadius: 2, minWidth: 2,
                  background: s === 'online'
                    ? `hsl(${220 + (i / SLOTS) * 30}, 100%, ${55 + (i / SLOTS) * 15}%)`
                    : s === 'offline' ? T.danger : T.bgCard,
                }} title={s === 'empty' ? 'Sem dados' : s === 'online' ? 'Online' : 'Offline'} />
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, padding: '0 2px' }}>
              <span style={{ fontSize: 9, color: T.textMuted }}>Últimos {totalReal > 0 ? totalReal : SLOTS} pings</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: uptimePct >= 90 ? T.success : uptimePct >= 50 ? T.warning : T.danger }}>{uptimePct}%</span>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <ActionBtn active={permsOpen} color={T.success} onClick={() => setPermsOpen(o => !o)} title="Ver permissões do macOS">
              Permissões{permsOpen ? ' ▲' : ''}
            </ActionBtn>
            <ActionBtn active={showTerminal} color={T.primary} onClick={() => setShowTerminal(s => !s)} title="Terminal remoto">
              Terminal{showTerminal ? ' ▲' : ''}
            </ActionBtn>
            <ActionBtn onClick={handleTest} disabled={testing} title="Testar conexão">
              {testing ? 'Testando...' : 'Testar'}
            </ActionBtn>
            <ActionBtn active={showLogs} color={T.warning} onClick={() => setShowLogs(s => !s)} title="Ver logs">
              Logs{showLogs ? ' ▲' : ''}
            </ActionBtn>
            {online && runner.mode === 'ui' && (
              <ActionBtn onClick={() => route(`/runner-v3/${id}/ui`)} title="Ver tela em tempo real">
                Tempo Real →
              </ActionBtn>
            )}
          </div>

          {/* Inline panels */}
          {permsOpen && (
            <div style={{
              padding: '16px', background: T.successBg, borderRadius: 14,
              border: `1px solid ${T.success}`, marginBottom: 16,
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                {permList.map(({ key, label, icon, desc }) => {
                  const granted = !!perms[key];
                  return (
                    <div key={key} title={desc} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', borderRadius: 10,
                      background: granted ? T.successBg : T.dangerBg,
                      border: `1px solid ${granted ? T.success : T.danger}`,
                      cursor: 'help',
                    }}>
                      <span style={{ fontSize: 18 }}>{icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{label}</div>
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: granted ? T.success : T.danger, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: granted ? T.success : T.danger }} />
                        {granted ? 'Ativa' : 'Negada'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {showTerminal && (
            <div style={{
              marginBottom: 16, borderRadius: 14, overflow: 'hidden',
              border: online ? `1px solid ${T.primary}` : `1px solid ${T.border}`,
              height: 400,
            }}>
              <Terminal runnerId={id} isOnline={online} />
            </div>
          )}

          {testResult && (
            <div style={{
              padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 12, fontWeight: 600,
              background: testResult.ok ? T.successBg : T.dangerBg,
              color: testResult.ok ? T.success : T.danger,
              border: `1px solid ${testResult.ok ? T.success : T.danger}`,
            }}>{testResult.msg}</div>
          )}
        </div>}
      </div>

      {/* ═══ 3. OPENCLAW SECTION — always visible ═══ */}
      <div style={{
        background: T.bgCard, border: `1px solid ${T.border}`,
        borderRadius: 14, marginBottom: 16, overflow: 'hidden',
      }}>
        <div onClick={() => toggleSection('openclaw')} style={{
          padding: '14px 20px', borderBottom: sectionOpen.openclaw ? `1px solid ${T.border}` : 'none',
          background: T.bgSubtle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none',
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.text, flex: 1 }}>OpenClaw</span>
          {openclawInstalled === true && (
            <span class="badge badge-success" style={{ fontSize: 10 }}>Instalado</span>
          )}
          {openclawInstalled === false && (
            <span class="badge badge-warning" style={{ fontSize: 10 }}>Não instalado</span>
          )}
          {openclawInstalled === null && (
            <span style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, background: T.bgCard, padding: '2px 8px', borderRadius: 10 }}>Verificando...</span>
          )}
          <span style={{ fontSize: 12, color: T.textMuted, transition: 'transform 0.2s', transform: sectionOpen.openclaw ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
        </div>
        {sectionOpen.openclaw && <div style={{ padding: '16px 20px' }}>
          {!(runner.os === 'macos' || runner.os === 'darwin' || runner.os === 'linux') ? (
            <div style={{ fontSize: 12, color: T.textMuted }}>OpenClaw não disponível para {runner.os}</div>
          ) : (<>
          {/* OpenClaw path info */}
          {openclawInstalled && runner.openclaw_path && (
            <div style={{
              marginBottom: 12, padding: '8px 12px', borderRadius: 8,
              background: T.accentBg, border: `1px solid ${T.accentBg}`,
              fontSize: 11, fontFamily: "'JetBrains Mono','Fira Code',monospace",
              color: T.textMuted, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ color: T.purple, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Path:</span>
              <code style={{ color: T.primary, wordBreak: 'break-all' }}>
                {runner.openclaw_path?.startsWith('docker:') 
                  ? `Docker (${runner.openclaw_path.replace('docker:', '')}) → /data/.openclaw` 
                  : runner.openclaw_path?.startsWith('data:') 
                    ? runner.openclaw_path.replace('data:', '') 
                    : runner.openclaw_path?.startsWith('systemd:')
                      ? `systemd → ${runner.openclaw_path.replace('systemd:', '')}`
                      : runner.openclaw_path}
              </code>
            </div>
          )}

          {/* Agent Card */}
          {selectedAgent && openclawInstalled && (
            <div
              onClick={() => setShowAgentPanel(true)}
              style={{
                marginBottom: 12, padding: '16px 20px', borderRadius: 12,
                background: `linear-gradient(135deg, ${T.accentBg}, ${T.successBg})`,
                border: `2px solid ${T.accentBgStrong}`,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
              onMouseEnter={(e: any) => {
                e.currentTarget.style.borderColor = T.accentBg;
                e.currentTarget.style.background = `linear-gradient(135deg, ${T.accentBg}, ${T.successBg})`;
              }}
              onMouseLeave={(e: any) => {
                e.currentTarget.style.borderColor = T.accentBg;
                e.currentTarget.style.background = `linear-gradient(135deg, ${T.accentBg}, ${T.successBg})`;
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <span style={{ fontSize: 28 }}>{selectedAgent.emoji}</span>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>
                    Agente — {selectedAgent.name || 'Sem nome'}
                  </h3>
                  <div style={{ fontSize: 12, color: T.accent, marginTop: 2 }}>
                    Agente sincronizado com OpenClaw
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#A0AEC0' }}>
                <span>Status: {selectedAgent.status}</span>
                {selectedAgent.defaultProvider && <span>LLM: {selectedAgent.defaultProvider}</span>}
                {selectedAgent.lastSync && <span>Sincronizado {new Date(selectedAgent.lastSync).toLocaleTimeString()}</span>}
              </div>
            </div>
          )}

          {/* Success message */}
          {successMsg && (
            <div style={{ padding: '10px 16px', borderRadius: 8, background: T.successBg, border: `1px solid ${T.successBg}`, color: T.success, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              {successMsg}
            </div>
          )}

          {/* Add Agent button */}
          {!showAgentPanel && selectedAgent && openclawInstalled && (
            <Button variant="ghost"
              onClick={() => setShowCreateAgentModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 18px', borderRadius: 10, marginBottom: 12,
                background: T.accentBg, border: `2px dashed ${T.accentBgStrong}`,
                color: T.accent, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                transition: 'all 0.2s', width: '100%',
              }}
              onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.accentBg; e.currentTarget.style.background = T.accentBg; }}
              onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.accentBg; e.currentTarget.style.background = T.accentBg; }}
            >
              Adicionar Agente
            </Button>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {openclawInstalled === true ? (<>
              <ActionBtn
                color={T.purple}
                onClick={async () => {
                  if (!machineIp) { alert('IP da máquina não encontrado.'); return; }
                  let tokenParam = '';
                  if (cloudMachineId) {
                    try {
                      const res = await api.get(`/cloud/machines/${cloudMachineId}/openclaw-config`) as any;
                      const tk = res?.data?.gatewayToken || res?.gatewayToken;
                      if (tk) tokenParam = `?token=${tk}`;
                    } catch {}
                  }
                  const panelUrl2 = `http://${machineIp}:${OPENCLAW_PANEL_PORT}/${tokenParam}`;
                  console.log('[OpenPanel2] URL:', panelUrl2);
                  console.log('[OpenPanel2] machineIp:', machineIp);
                  console.log('[OpenPanel2] tokenParam:', tokenParam);
                  window.open(panelUrl2, '_blank', 'noopener,noreferrer');
                }}
                disabled={!online || !machineIp}
                title={!machineIp ? 'IP não encontrado' : online ? `Abrir painel OpenClaw (${machineIp}:${OPENCLAW_PANEL_PORT})` : 'Máquina offline — painel indisponível'}
              >
                Painel{!machineIp ? '' : !online ? '' : ''}
              </ActionBtn>
              {cloudMachineId && (
                <ActionBtn
                  color={tokenCopied ? T.success : tokenError ? T.danger : T.purple}
                  onClick={handleCopyGatewayToken}
                  disabled={copyingToken}
                  title="Copiar Gateway Token para o clipboard"
                >
                  {copyingToken ? 'Copiando...' : tokenCopied ? 'Copiado!' : tokenError ? `${tokenError}` : 'Gateway Token'}
                </ActionBtn>
              )}
              <Button variant="ghost" title="Remover OpenClaw" onClick={() => { if (installBusy) return; closeAllOC(); setShowUninstaller(s => !s); }} style={{
                padding: '8px 14px', borderRadius: 10, cursor: installBusy ? 'not-allowed' : 'pointer',
                fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
                border: showUninstaller ? `1px solid ${T.danger}` : '1px solid transparent',
                background: showUninstaller ? T.dangerBg : 'transparent',
                color: T.danger, opacity: installBusy ? 0.5 : 1,
              }}
                onMouseEnter={(e: any) => { if (!showUninstaller) e.currentTarget.style.color = T.danger; }}
                onMouseLeave={(e: any) => { if (!showUninstaller) e.currentTarget.style.color = T.danger; }}
              >Desinstalar{showUninstaller ? ' ▲' : ''}</Button>
              <ActionBtn color={T.purple} active={showSkills} onClick={handleManageSkills}>
                Gerenciar Habilidades{showSkills ? ' ▲' : ' ▼'}
              </ActionBtn>
            </>) : openclawInstalled === false ? (
              <Button variant="ghost" title="Instalar OpenClaw" onClick={() => { if (installBusy) return; setShowUninstaller(false); setShowInstaller(s => !s); }} class={showInstaller ? '' : 'btn btn-primary'} style={{
                ...(showInstaller ? {
                  padding: '8px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: T.accentBg, color: T.purple,
                  border: `1px solid ${T.accentBgStrong}`, transition: 'all 0.15s',
                } : { fontSize: 13 }),
                opacity: installBusy ? 0.5 : 1,
              }}>Instalar OpenClaw{showInstaller ? ' ▲' : ''}</Button>
            ) : null}
          </div>
          </>)}
        </div>}
      </div>

      {/* ═══ ERROR ALERT (last 24h) — below all 3 sections ═══ */}
      {(() => {
        const errors24h = logEntries.filter(l => !l.ok);
        if (errors24h.length === 0 || errorAlertDismissed) return null;

        const byType: Record<string, number> = {};
        errors24h.forEach(e => { byType[e.type] = (byType[e.type] || 0) + 1; });
        const lastError = errors24h[errors24h.length - 1];
        const lastTime = lastError ? new Date(lastError.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';

        return (
          <div style={{
            padding: '14px 18px', background: T.dangerBg, borderRadius: 14,
            border: `1px solid ${T.danger}`, marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.warning }}>!</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.danger }}>
                  {errors24h.length} erro{errors24h.length > 1 ? 's' : ''} nas últimas 24h
                </span>
                <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 8 }}>
                  Último: {lastTime}
                </span>
              </div>
              <Button variant="ghost" onClick={() => setErrorAlertExpanded(e => !e)} style={{
                background: T.dangerBg, border: `1px solid ${T.dangerBg}`,
                borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 600,
                color: T.danger, cursor: 'pointer',
              }}>{errorAlertExpanded ? 'Recolher ▲' : 'Expandir ▼'}</Button>
              <Button variant="ghost" onClick={() => setErrorAlertDismissed(true)} style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: 16, color: T.textMuted, padding: '2px 6px', lineHeight: 1,
              }}></Button>
            </div>

            {!errorAlertExpanded && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {Object.entries(byType).map(([type, count]) => (
                  <span key={type} style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6,
                    background: T.dangerBg, color: T.danger,
                    border: `1px solid ${T.danger}`,
                  }}>{type}: {count}×</span>
                ))}
              </div>
            )}

            {errorAlertExpanded && (
              <div style={{ marginTop: 10, maxHeight: 200, overflowY: 'auto' }}>
                {errors24h.slice().reverse().slice(0, 20).map((err, i) => {
                  const t = new Date(err.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                  return (
                    <div key={i} style={{
                      display: 'flex', gap: 8, alignItems: 'center', padding: '5px 8px',
                      borderRadius: 6, fontSize: 12,
                      background: i % 2 === 0 ? 'transparent' : T.dangerBg,
                    }}>
                      <span style={{ fontFamily: T.fontMono, fontSize: 10, color: T.textMuted, minWidth: 60 }}>{t}</span>
                      <span style={{ fontWeight: 600, color: T.danger, minWidth: 80 }}>{err.type}</span>
                      <span style={{ color: T.textMuted, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {err.details || err.error || 'Falha na operação'}
                      </span>
                    </div>
                  );
                })}
                {errors24h.length > 20 && (
                  <div style={{ textAlign: 'center', fontSize: 11, color: T.textMuted, padding: 6 }}>
                    +{errors24h.length - 20} erros mais antigos
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* OpenClaw sub-panels */}
      {showInstaller && (
        <OpenClawInstaller runnerId={id} isOnline={online} runnerOs={runner.os}
          openclawPath={runner.openclaw_path || undefined}
          onClose={() => { setShowInstaller(false); setInstallBusy(false); checkOpenClaw(); }}
          onBusyChange={setInstallBusy} />
      )}
      {/* Agent Panel Modal */}
      {showAgentPanel && selectedAgent && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'transparent', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }} onClick={() => setShowAgentPanel(false)}>
          <div style={{
            background: T.bgCard, borderRadius: 16, width: '90%', maxWidth: 900,
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            border: `1px solid ${T.accentBgStrong}`,
          }} onClick={(e: any) => e.stopPropagation()}>
            {/* Header */}
            <div style={{
              padding: '20px 24px', borderBottom: `1px solid ${T.bgActive}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: T.text }}>
                {selectedAgent.emoji} {selectedAgent.name}
              </h2>
              <Button variant="ghost" onClick={() => setShowAgentPanel(false)} style={{
                background: 'none', border: 'none', color: T.accent, fontSize: 24, cursor: 'pointer',
              }}></Button>
            </div>
            {/* Tabs */}
            <div style={{
              display: 'flex', gap: 0, padding: '0 24px', borderBottom: `1px solid ${T.bgActive}`,
              background: T.bgOverlay,
            }}>
              {[
                { id: 'details', label: 'Detalhes' },
                { id: 'config', label: 'Inteligência' },
                { id: 'brain', label: 'Cérebro' },
                { id: 'integrations', label: 'Ferramentas' },
                { id: 'skills', label: 'Habilidades' },
              ].map((tab) => (
                <Button variant="ghost" key={tab.id} onClick={() => setAgentPanelTab(tab.id as any)} style={{
                  padding: '12px 16px', border: 'none',
                  background: agentPanelTab === tab.id ? T.accentBg : 'transparent',
                  color: agentPanelTab === tab.id ? T.primary : T.textMuted,
                  cursor: 'pointer', fontSize: 13, fontWeight: agentPanelTab === tab.id ? 700 : 500,
                  borderBottom: agentPanelTab === tab.id ? '2px solid #6366F1' : 'none',
                  transition: 'all 0.2s',
                }}>
                  {tab.label}
                </Button>
              ))}
            </div>
            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {agentPanelTab === 'details' && (
                <AgentGeneral runnerId={id} isOnline={online} openclawPath={runner.openclaw_path || undefined} cloudMachineId={cloudMachineId || undefined} agentInfo={selectedAgent ? { name: selectedAgent.name, emoji: selectedAgent.emoji, status: selectedAgent.status, defaultProvider: selectedAgent.defaultProvider, defaultModel: selectedAgent.defaultModel, lastSync: selectedAgent.lastSync } : undefined} />
              )}
              {agentPanelTab === 'config' && (
                <OpenClawConfig runnerId={id} isOnline={online} openclawPath={runner.openclaw_path || undefined} cloudMachineId={cloudMachineId || undefined} />
              )}
              {agentPanelTab === 'brain' && (
                <OpenClawBrain runnerId={id} isOnline={online} openclawPath={runner.openclaw_path || undefined} openclawBase={runner.openclaw_base || undefined} cloudMachineId={cloudMachineId || undefined} onClose={() => setShowAgentPanel(false)} />
              )}
              {agentPanelTab === 'integrations' && (
                <AgentCredentials runnerId={id} isOnline={online} openclawPath={runner.openclaw_path || undefined} cloudMachineId={cloudMachineId || undefined} />
              )}
              {agentPanelTab === 'skills' && (
                <OpenClawSkills runnerId={id} isOnline={online} openclawPath={runner.openclaw_path || undefined} cloudMachineId={cloudMachineId || undefined} onClose={() => setShowAgentPanel(false)} />
              )}
            </div>
          </div>
        </div>
      )}
      {showUninstaller && (
        <OpenClawUninstaller runnerId={id} isOnline={online} runnerOs={runner.os}
          openclawPath={runner.openclaw_path || undefined}
          onClose={() => { setShowUninstaller(false); setInstallBusy(false); }}
          onUninstalled={() => { setOpenclawInstalled(false); setInstallBusy(false); }}
          onBusyChange={setInstallBusy} />
      )}

      {/* ═══ SKILLS SECTION (New Grid UX) — Only show when opened ═══ */}
      {openclawInstalled && showSkills && (
        <div ref={skillsSectionRef} style={{
          padding: '20px', background: T.bgCard, borderRadius: 14,
          border: `1px solid ${T.purple}`, marginBottom: 16,
          transition: 'box-shadow 0.3s ease',
          boxShadow: skillsHighlight ? `0 0 20px ${T.purple}, inset 0 0 20px ${T.purple}` : 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, color: T.textMuted }}>Skills</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Habilidades do OpenClaw</span>
            </div>
            <Button variant="ghost" onClick={() => setShowSkills(false)} style={{
              background: 'none', border: 'none', color: T.accent, fontSize: 20, cursor: 'pointer',
              padding: '4px 8px', transition: 'all 0.15s',
            }} onMouseEnter={(e: any) => e.currentTarget.style.color = T.primary} onMouseLeave={(e: any) => e.currentTarget.style.color = T.accent}>
                          </Button>
          </div>
          <SkillsCategoryTabs machineId={id} isOnline={online} />
        </div>
      )}

      {/* ═══ RECONNECT HELP (offline) ═══ */}
      {!online && (
        <div style={{
          padding: '20px', background: T.bgCard, borderRadius: 14,
          border: `1px solid ${T.warningBgStrong}`, marginBottom: 20,
        }}>
          <p style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: '0 0 16px' }}>Como reconectar</p>
          <div style={{ marginBottom: 16 }}>
 <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: '0 0 6px' }}>1⃣ Abra o app normalmente</p>
            <p style={{ fontSize: 12, color: T.textMuted, margin: '0 0 8px' }}>Abra o <strong>WhiteLabelClaw</strong> pela pasta Aplicações ou Launchpad.</p>
          </div>
          <div style={{ marginBottom: 16 }}>
 <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: '0 0 6px' }}>2⃣ Ou rode no Terminal:</p>
            <CopyCommand cmd="/Applications/WhiteLabelClaw.app/Contents/MacOS/elon-runner" />
          </div>
          <div style={{ marginBottom: 16 }}>
 <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: '0 0 6px' }}>3⃣ Se der erro de "await":</p>
            <CopyCommand cmd={`sed -i '' 's/await fetch/fetch/' /Applications/WhiteLabelClaw.app/Contents/Resources/app-launcher.js && /Applications/WhiteLabelClaw.app/Contents/MacOS/elon-runner`} />
          </div>
          <div style={{ marginBottom: 12 }}>
 <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: '0 0 6px' }}>4⃣ Se nada funcionar, reinstale:</p>
            <CopyCommand cmd="open /download/macos" />
          </div>
          <div style={{
            padding: '10px 14px', background: T.primaryBg, borderRadius: 10,
            border: `1px solid ${T.primary}`, fontSize: 12, color: T.textMuted,
          }}>
 <strong>Dica:</strong>Ver logs: <code style={{ background: T.bgCardHover, padding: '2px 6px', borderRadius: 4, fontFamily: T.fontMono, fontSize: 11 }}>cat ~/.elon-runner/runner.log | tail -20</code>
          </div>
        </div>
      )}

      {/* ═══ DANGER ZONE (discrete) ═══ */}
      <div class="v2-danger-zone" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <Button variant="ghost" onClick={() => setShowDelete(true)} class="v2-delete-btn" style={{
          padding: '6px 14px', background: 'transparent',
          color: T.textMuted, border: '1px solid transparent',
          borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 500,
          transition: 'all 0.2s',
        }}>Excluir máquina</Button>
      </div>

      {/* ═══ DELETE MODAL ═══ */}
      {showDelete && (
        <div class="modal-overlay" onClick={() => !deleting && setShowDelete(false)}>
          <div style={{
            background: T.bgCard, borderRadius: 16,
            padding: '28px 32px', maxWidth: 420, width: '90%',
            border: `1px solid ${T.border}`, boxShadow: `0 20px 60px ${T.blackAlpha40}`,
            animation: 'slideUp 0.2s ease',
          }} onClick={(e: any) => e.stopPropagation()}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 20, marginBottom: 12, color: T.textMuted }}>Excluir</div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, margin: '0 0 8px' }}>Excluir Máquina</h3>
              <p style={{ fontSize: 14, color: T.textMuted, margin: 0 }}>
                Tem certeza que deseja excluir <strong style={{ color: T.text }}>{runner.name}</strong>?
              </p>
              <p style={{ fontSize: 12, color: T.danger, marginTop: 12 }}>Esta ação não pode ser desfeita.</p>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <Button variant="ghost" onClick={() => setShowDelete(false)} disabled={deleting} class="btn btn-secondary">Cancelar</Button>
              <Button variant="ghost" onClick={handleDelete} disabled={deleting} class="btn btn-danger" style={{ opacity: deleting ? 0.7 : 1 }}>
                {deleting ? 'Excluindo...' : 'Excluir'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Agent Modal */}
      {showCreateAgentModal && (
        <SetupModal
          mode="create"
          machineId={cloudMachineId || id}
          runnerId={id}
          onClose={() => setShowCreateAgentModal(false)}
          onSuccess={() => {
            setShowCreateAgentModal(false);
            setSuccessMsg('Agente criado com sucesso! Processamento em breve.');
            setTimeout(() => setSuccessMsg(''), 6000);
            fetchAgentRequests();
          }}
        />

      )}


      {/* Agent Requests List */}
      {agentRequests.length > 0 && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 900, background: T.bgCard, borderRadius: 12, padding: 16, border: `1px solid ${T.accentBg}`, maxWidth: 360, maxHeight: 300, overflowY: 'auto', boxShadow: '0 8px 32px var(--t-black-alpha-30)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 10 }}>Pedidos de Agente</div>
          {agentRequests.map((r: any) => {
            const statusColors: Record<string, string> = { pending: T.warning, approved: T.success, processing: T.primary, completed: T.success, failed: T.danger, rejected: T.textMuted, cancelled: T.textMuted };
            const statusIcons: Record<string, string> = { pending: '•', approved: '', processing: '•', completed: '', failed: '×', rejected: '×', cancelled: '×' };
            return (
              <div key={r.id} style={{ padding: '8px 0', borderBottom: `1px solid ${T.bgCardHover}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{r.agent_name}</span>
                  <span style={{ fontSize: 10, color: statusColors[r.status] || T.textMuted, fontWeight: 600 }}>
 {statusIcons[r.status] || ''} {r.status}
                  </span>
                </div>
                {r.stage && r.status === 'processing' && (
 <div style={{ fontSize: 10, color: T.accent, marginTop: 4 }}> {r.stage_message || r.stage}</div>
                )}
                {r.error_message && r.status === 'failed' && (
                  <div style={{ fontSize: 10, color: T.danger, marginTop: 4 }}>{r.error_message}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .v2-danger-zone { opacity: 0.35; transition: opacity 0.2s; }
        .v2-danger-zone:hover { opacity: 1; }
        .v2-delete-btn:hover { color: var(--error) !important; border-color: ${T.dangerBgStrong} !important; }
      `}</style>
    </div>
  );
}
